import { z } from "zod";
import type { Department } from "@/lib/auth-context";

export type FieldType = "text" | "url" | "number" | "select" | "textarea" | "date";

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: readonly string[];
  placeholder?: string;
  optional?: boolean;
  /** Stored under details JSONB; otherwise it's a top-level submission column */
  inDetails?: boolean;
  /** column width hint for table */
  showInTable?: boolean;
  hint?: string;
}

export interface DepartmentConfig {
  department: Department;
  label: string;
  itemNoun: string;          // "video", "design", "campaign"
  ctaLabel: string;          // "+ New Entry"
  /** Whether the department uses brand+product pickers */
  usesBrandProduct: boolean;
  /** Field definitions (excluding date/title/brand/product which are handled specially) */
  detailFields: FieldDef[];
  /** Schema for the details JSONB */
  detailsSchema: z.ZodType<Record<string, unknown>>;
  /** Friendly title field label, e.g. "Video title" */
  titleLabel: string;
  /** KPI labels customised per department */
  kpis: {
    outputLabel: string;     // "Output This Month"
  };
}

const VIDEO_TYPES = ["Short", "Long Form", "Reel", "Ad", "Tutorial", "Review"] as const;
const DESIGN_TYPES = ["Thumbnail", "Banner", "Social Post", "Ad Creative", "Packaging", "Logo"] as const;
const CAMPAIGN_CHANNELS = ["Meta Ads", "Google Ads", "Email", "Influencer", "Affiliate", "Other"] as const;
const SEO_TASK_TYPES = ["Keyword Research", "On-Page", "Backlink", "Technical Audit", "Content Brief"] as const;
const SMM_PLATFORMS = ["Instagram", "YouTube", "TikTok", "Facebook", "X / Twitter", "LinkedIn"] as const;
const SCRIPT_FORMATS = ["Hook + Script", "Storyboard", "VO Script", "Ad Script"] as const;
const CALL_OUTCOMES = ["Connected", "Voicemail", "No Answer", "Callback", "Not Interested", "Sale"] as const;
const HR_TASKS = ["Interview", "Onboarding", "Offboarding", "Payroll", "Policy", "Performance Review"] as const;

const baseEditor: DepartmentConfig = {
  department: "editor",
  label: "Editor",
  itemNoun: "video",
  ctaLabel: "+ New Entry",
  usesBrandProduct: true,
  titleLabel: "Video title",
  kpis: { outputLabel: "Videos This Month" },
  detailFields: [
    { key: "video_type", label: "Video type", type: "select", options: VIDEO_TYPES, showInTable: true, inDetails: true },
    { key: "duration_seconds", label: "Duration (seconds)", type: "number", inDetails: true, showInTable: true },
    { key: "video_link", label: "Video link", type: "url", optional: true, inDetails: true, placeholder: "https://..." },
  ],
  detailsSchema: z.object({
    video_type: z.string().min(1),
    duration_seconds: z.coerce.number().int().min(0).max(36000),
    video_link: z.string().url().optional().or(z.literal("")),
  }),
};

const graphic: DepartmentConfig = {
  department: "graphic_designer",
  label: "Graphic Designer",
  itemNoun: "design",
  ctaLabel: "+ New Design",
  usesBrandProduct: true,
  titleLabel: "Design name",
  kpis: { outputLabel: "Designs This Month" },
  detailFields: [
    { key: "design_type", label: "Design type", type: "select", options: DESIGN_TYPES, showInTable: true, inDetails: true },
    { key: "variants", label: "Variants", type: "number", inDetails: true, showInTable: true, hint: "Number of variants delivered" },
    { key: "file_link", label: "File link", type: "url", optional: true, inDetails: true, placeholder: "Drive / Figma URL" },
  ],
  detailsSchema: z.object({
    design_type: z.string().min(1),
    variants: z.coerce.number().int().min(1).max(50),
    file_link: z.string().url().optional().or(z.literal("")),
  }),
};

const marketer: DepartmentConfig = {
  department: "marketer",
  label: "Marketer",
  itemNoun: "campaign",
  ctaLabel: "+ New Campaign",
  usesBrandProduct: true,
  titleLabel: "Campaign name",
  kpis: { outputLabel: "Campaigns This Month" },
  detailFields: [
    { key: "channel", label: "Channel", type: "select", options: CAMPAIGN_CHANNELS, showInTable: true, inDetails: true },
    { key: "spend", label: "Spend (₹)", type: "number", inDetails: true, showInTable: true },
    { key: "roas", label: "ROAS", type: "number", inDetails: true, showInTable: true, hint: "Return on ad spend" },
    { key: "notes", label: "Notes", type: "textarea", optional: true, inDetails: true },
  ],
  detailsSchema: z.object({
    channel: z.string().min(1),
    spend: z.coerce.number().min(0),
    roas: z.coerce.number().min(0),
    notes: z.string().optional().or(z.literal("")),
  }),
};

const seo: DepartmentConfig = {
  department: "seo",
  label: "SEO",
  itemNoun: "task",
  ctaLabel: "+ New Task",
  usesBrandProduct: true,
  titleLabel: "Task title",
  kpis: { outputLabel: "Tasks This Month" },
  detailFields: [
    { key: "task_type", label: "Task type", type: "select", options: SEO_TASK_TYPES, showInTable: true, inDetails: true },
    { key: "keyword", label: "Primary keyword", type: "text", inDetails: true, showInTable: true },
    { key: "page_url", label: "Page URL", type: "url", optional: true, inDetails: true },
  ],
  detailsSchema: z.object({
    task_type: z.string().min(1),
    keyword: z.string().min(1),
    page_url: z.string().url().optional().or(z.literal("")),
  }),
};

const smm: DepartmentConfig = {
  department: "smm",
  label: "SMM",
  itemNoun: "post",
  ctaLabel: "+ New Post",
  usesBrandProduct: true,
  titleLabel: "Post title / caption",
  kpis: { outputLabel: "Posts This Month" },
  detailFields: [
    { key: "platform", label: "Platform", type: "select", options: SMM_PLATFORMS, showInTable: true, inDetails: true },
    { key: "post_link", label: "Post link", type: "url", optional: true, inDetails: true },
    { key: "reach", label: "Reach", type: "number", inDetails: true, showInTable: true },
  ],
  detailsSchema: z.object({
    platform: z.string().min(1),
    post_link: z.string().url().optional().or(z.literal("")),
    reach: z.coerce.number().int().min(0),
  }),
};

const scriptwriter: DepartmentConfig = {
  department: "scriptwriter",
  label: "Scriptwriter",
  itemNoun: "script",
  ctaLabel: "+ New Script",
  usesBrandProduct: true,
  titleLabel: "Script title",
  kpis: { outputLabel: "Scripts This Month" },
  detailFields: [
    { key: "format", label: "Format", type: "select", options: SCRIPT_FORMATS, showInTable: true, inDetails: true },
    { key: "word_count", label: "Word count", type: "number", inDetails: true, showInTable: true },
    { key: "doc_link", label: "Doc link", type: "url", optional: true, inDetails: true },
  ],
  detailsSchema: z.object({
    format: z.string().min(1),
    word_count: z.coerce.number().int().min(0),
    doc_link: z.string().url().optional().or(z.literal("")),
  }),
};

const calling: DepartmentConfig = {
  department: "calling",
  label: "Calling",
  itemNoun: "call",
  ctaLabel: "+ Log Call",
  usesBrandProduct: false,
  titleLabel: "Customer name",
  kpis: { outputLabel: "Calls This Month" },
  detailFields: [
    { key: "phone", label: "Phone", type: "text", inDetails: true, showInTable: true },
    { key: "outcome", label: "Outcome", type: "select", options: CALL_OUTCOMES, showInTable: true, inDetails: true },
    { key: "duration_minutes", label: "Duration (min)", type: "number", inDetails: true, showInTable: true },
    { key: "notes", label: "Notes", type: "textarea", optional: true, inDetails: true },
  ],
  detailsSchema: z.object({
    phone: z.string().min(4),
    outcome: z.string().min(1),
    duration_minutes: z.coerce.number().int().min(0).max(600),
    notes: z.string().optional().or(z.literal("")),
  }),
};

const hr: DepartmentConfig = {
  department: "hr",
  label: "HR",
  itemNoun: "task",
  ctaLabel: "+ New HR Task",
  usesBrandProduct: false,
  titleLabel: "Task title",
  kpis: { outputLabel: "Tasks This Month" },
  detailFields: [
    { key: "task_type", label: "Task type", type: "select", options: HR_TASKS, showInTable: true, inDetails: true },
    { key: "person", label: "Person / candidate", type: "text", inDetails: true, showInTable: true },
    { key: "notes", label: "Notes", type: "textarea", optional: true, inDetails: true },
  ],
  detailsSchema: z.object({
    task_type: z.string().min(1),
    person: z.string().min(1),
    notes: z.string().optional().or(z.literal("")),
  }),
};

const admin: DepartmentConfig = {
  department: "admin",
  label: "Admin",
  itemNoun: "task",
  ctaLabel: "+ New Task",
  usesBrandProduct: false,
  titleLabel: "Description",
  kpis: { outputLabel: "Tasks This Month" },
  detailFields: [],
  detailsSchema: z.object({}),
};

const aiDept: DepartmentConfig = {
  ...graphic,
  department: "ai",
  label: "AI",
  itemNoun: "creative",
  ctaLabel: "+ New Creative",
  titleLabel: "Creative name",
  kpis: { outputLabel: "Creatives This Month" },
};

export const DEPARTMENT_CONFIGS: Record<Department, DepartmentConfig> = {
  admin,
  editor: baseEditor,
  graphic_designer: graphic,
  ai: aiDept,
  marketer,
  seo,
  smm,
  scriptwriter,
  calling,
  hr,
};

export function getDepartmentConfig(d: Department) {
  return DEPARTMENT_CONFIGS[d];
}

export function buildSubmissionSchema(cfg: DepartmentConfig) {
  const base: Record<string, z.ZodTypeAny> = {
    title: z.string().min(2, "Too short").max(200),
  };
  if (cfg.usesBrandProduct) {
    base.brand = z.string().min(1, "Select a brand");
    base.product = z.string().min(1, "Select a product");
  }
  return z.object(base).and(cfg.detailsSchema);
}
